import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Bug } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useLocation } from "react-router-dom";
import { BugReportForm } from "./BugReportForm";
import { useBugReports } from "@/hooks/useBugReports";
import { useToast } from "@/hooks/use-toast";
import { useUserRoles } from "@/hooks/useUserRoles";
import html2canvas from "html2canvas";
import { Progress } from "@/components/ui/progress";

export function BugReportButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressOverlayRef = useRef<HTMLDivElement | null>(null);
  
  const location = useLocation();
  const { toast } = useToast();
  const { useCreateBugReportMutation } = useBugReports();
  const createMutation = useCreateBugReportMutation();
  const { useHasRoleQuery } = useUserRoles();
  
  const { data: isDeveloper = false } = useHasRoleQuery("DEVELOPER");
  const { data: isAdmin = false } = useHasRoleQuery("ADMIN");
  const { data: isTester = false } = useHasRoleQuery("TESTER");
  
  const hasAccess = Boolean(isDeveloper || isAdmin || isTester);
  
  

  useEffect(() => {
    const path = location.pathname;
    
    const primarySection = path.split('/')[1];
    if (primarySection) {
      setCurrentLocation(primarySection.charAt(0).toUpperCase() + primarySection.slice(1));
    } else {
      setCurrentLocation("Dashboard");
    }
  }, [location]);

  useEffect(() => {
    if (progressOverlayRef.current) {
      if (isProcessing) {
        progressOverlayRef.current.style.display = "flex";
      } else {
        progressOverlayRef.current.style.display = "none";
      }
    }
  }, [isProcessing]);

  if (!hasAccess) {
    
    return null;
  }

  const captureScreenshot = async () => {
    try {
      setIsOpen(false);
      
      toast({
        title: "Taking screenshot",
        description: "Please don't click anywhere until the process is complete.",
      });
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setIsProcessing(true);
      setProgress(0);
      
      const canvas = await html2canvas(document.body, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
      });
      
      return new Promise<File>((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
            
            const progressInterval = setInterval(() => {
              setProgress(prev => {
                const newProgress = prev + 10;
                if (newProgress >= 100) {
                  clearInterval(progressInterval);
                  setTimeout(() => {
                    setScreenshots([file]);
                    setIsOpen(true);
                    setIsProcessing(false);
                  }, 200);
                  resolve(file);
                  return 100;
                }
                return newProgress;
              });
            }, 100);
          } else {
            setIsOpen(true);
            setIsProcessing(false);
            toast({
              title: "Screenshot failed",
              description: "Could not capture the screenshot. You can still submit the report.",
              variant: "destructive",
            });
          }
        }, 'image/png', 0.9);
      });
    } catch (error) {
      console.error("Failed to capture screenshot:", error);
      toast({
        title: "Screenshot failed",
        description: "Could not capture the screenshot. You can still submit the report.",
        variant: "destructive",
      });
      setIsOpen(true);
      setIsProcessing(false);
      return null;
    }
  };

  const handleButtonClick = () => {
    setScreenshots([]);
    setIsOpen(true);
  };

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const formData = {
        ...data,
        location: data.location || currentLocation,
        screenshots: [...(data.screenshots || []), ...screenshots]
      };
      
      await createMutation.mutateAsync(formData);
      
      toast({
        title: "Report submitted",
        description: "Thank you for your feedback! The report has been submitted successfully.",
      });
      
      setIsOpen(false);
      setScreenshots([]);
    } catch (error) {
      console.error("Error submitting bug report:", error);
      toast({
        title: "Submission failed",
        description: "Failed to submit the bug report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        className="fixed right-4 top-1/2 -translate-y-1/2 z-50 rounded-full shadow-lg bg-destructive hover:bg-destructive/90 text-destructive-foreground"
        onClick={handleButtonClick}
        title="Report a bug or issue"
      >
        <Bug className="h-5 w-5" />
      </Button>

      <div 
        id="bug-report-progress-overlay"
        ref={progressOverlayRef}
        className="fixed inset-0 bg-background/80 backdrop-blur-sm flex flex-col items-center justify-center z-[100]"
        style={{ display: isProcessing ? 'flex' : 'none' }}
      >
        <div className="w-full max-w-md p-6 rounded-lg bg-card shadow-lg">
          <h3 className="text-lg font-medium mb-2">Processing Screenshot</h3>
          <p className="text-muted-foreground mb-4">Preparing bug report form...</p>
          <Progress value={progress} className="h-2 mb-2" />
          <p className="text-sm text-right text-muted-foreground">{progress}%</p>
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-6">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-xl">Report a Bug or Improvement</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Submit details about any issues or suggestions for improvements you've encountered.
            </DialogDescription>
          </DialogHeader>
          
          <BugReportForm 
            onSubmit={handleSubmit} 
            isSubmitting={isSubmitting}
            initialLocation={currentLocation}
            initialScreenshots={screenshots}
            onTakeScreenshot={captureScreenshot}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
